import { Badge, Button, Text } from '@elladx/design-system';
import { useEffect, useRef } from 'react';
import Modal from './Modal';
import { formatDateTime } from '../utils/dateFormat';
import type { TrackingInfo } from '../types';
interface TrackingDetailsModalProps {
    isOpen: boolean;
    loading: boolean;
    tracking: TrackingInfo | null;
    errorMessage?: string;
    onClose: () => void;
    onRetry?: () => void;
}
function safeExternalTrackingUrl(value?: string | null): string | null {
    if (!value)
        return null;
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && !url.username && !url.password ? url.toString() : null;
    }
    catch {
        return null;
    }
}
export function TrackingDetailsContent({ loading, tracking, errorMessage = '', onRetry }: Pick<TrackingDetailsModalProps, 'loading' | 'tracking' | 'errorMessage' | 'onRetry'>) {
    const errorRef = useRef<HTMLElement | null>(null);
    useEffect(() => {
        if (errorMessage) errorRef.current?.scrollIntoView({ block: 'nearest' });
    }, [errorMessage]);
    return (<div className="management-detail">
      {loading ? (<div role="status" style={{ display: 'grid', gap: '12px' }}><Text>Loading tracking…</Text>
          <div className="skeleton" style={{ height: '40px' }}/>
          <div className="skeleton" style={{ height: '100px' }}/>
        </div>) : errorMessage ? (<>
          <Text ref={errorRef} role="alert" tone="error" style={{ color: 'var(--dx-color-error)', marginBottom: '20px' }}>{errorMessage}</Text>
          {onRetry && <div className="dialog-actions"><Button type="button" variant="secondary" onClick={onRetry}>Retry</Button></div>}
        </>) : tracking ? (<div>
          <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
            <div>
              <strong>Tracking Number:</strong>
              <div style={{ marginTop: '4px' }}>{tracking.tracking_code}</div>
            </div>
            <div>
              <strong>Carrier:</strong> {tracking.carrier}
            </div>
            <div>
              <strong>Status:</strong>{' '}
              <Badge className={`status-badge ${tracking.status_tone || "info"}`}>{tracking.status_label}</Badge>
            </div>
            {tracking.est_delivery_date && (<div>
                <strong>Estimated Delivery:</strong>{' '}
                {new Date(tracking.est_delivery_date).toLocaleDateString()}
              </div>)}
          </div>

          {tracking.tracking_details && tracking.tracking_details.length > 0 && (<div>
              <strong style={{ display: 'block', marginBottom: '12px' }}>Tracking History:</strong>
              <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    background: 'var(--dx-color-sage)',
                    padding: '12px'
                }}>
                {tracking.tracking_details.map((detail, i) => {
                    const structuredLocation = detail.tracking_location
                        ? [
                            detail.tracking_location.city,
                            detail.tracking_location.state,
                            detail.tracking_location.zip,
                            detail.tracking_location.country,
                        ]
                            .filter(Boolean)
                            .join(', ')
                        : '';
                    const location = detail.location || structuredLocation;
                    return (<div key={`${detail.datetime}-${i}`} style={{
                            padding: '12px 0',
                            borderBottom: i < tracking!.tracking_details.length - 1
                                ? '1px solid var(--dx-color-border)'
                                : 'none'
                        }}>
                      <div style={{ color: 'var(--dx-color-muted)' }}>
                        {formatDateTime(detail.datetime)}
                      </div>
                      <div style={{ fontWeight: 500, marginTop: '4px' }}>{detail.message}</div>
                      {location && (<div style={{ color: 'var(--dx-color-muted)', marginTop: '2px' }}>
                          {location}
                        </div>)}
                    </div>);
                })}
              </div>
            </div>)}
          {(tracking.tracking_details?.length ?? 0) === 0 && <Text>No tracking events yet.</Text>}

          {safeExternalTrackingUrl(tracking.public_url || tracking.tracking_url) && (<div style={{ marginTop: '20px' }}>
              <Button asChild={true} variant="secondary"><a href={safeExternalTrackingUrl(tracking.public_url || tracking.tracking_url) || undefined} target="_blank" rel="noopener noreferrer">
                View on Carrier Website
              </a></Button>
            </div>)}
        </div>) : (<Text>No tracking information available.</Text>)}
    </div>);
}
function TrackingDetailsModal({ isOpen, onClose, ...props }: TrackingDetailsModalProps) {
    return <Modal isOpen={isOpen} onClose={onClose} title="Tracking Details"><TrackingDetailsContent {...props}/></Modal>;
}
export default TrackingDetailsModal;
